import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Star } from "lucide-react";
import { sendNotification } from "@/lib/emailService";
import { useTranslation } from "react-i18next";

export function ReviewForm() {
  const { t, i18n } = useTranslation();
  const [rating, setRating] = useState(5);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    reviewer_name: '',
    comment: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Create localized comment object with auto-translation
      // This will generate { "en": "...", "it": "...", etc }
      const currentLang = i18n.language.split('-')[0] || 'en';

      // Import dynamically to avoid circular dependencies if any (though here it's fine static)
      const { generateLocalizedReview } = await import('@/lib/autoTranslate');
      const localizedComment = await generateLocalizedReview(formData.comment, currentLang);

      const { error } = await supabase
        .from('reviews')
        .insert([{
          reviewer_name: formData.reviewer_name,
          rating: rating,
          comment: localizedComment as any,
          is_approved: false
        }]);

      if (error) throw error;

      toast({
        title: t('reviews.submittedTitle', "Review submitted!"),
        description: t('reviews.submittedDesc', "Thank you! Your review will be visible after approval."),
      });

      // Send email notification with Premium Review Card
      const starString = '★'.repeat(rating) + '☆'.repeat(5 - rating);

      await sendNotification(
        `New Review: ${rating} Stars from ${formData.reviewer_name}`,
        "New Review Pending Approval",
        `<div class="info-box text-center">
            <div class="info-label">Rating</div>
            <div class="stars" style="margin: 10px 0; font-size:24px;">${starString}</div>
            <div class="info-value">${rating} / 5.0</div>
         </div>
         
         <div class="section-title">Review Details</div>
         <table width="100%">
            <tr>
                <td class="info-row">
                    <span class="info-label">Reviewer Name</span>
                    <div class="info-value">${formData.reviewer_name}</div>
                </td>
            </tr>
         </table>
         
         <div class="section-title">Comment (${currentLang})</div>
         <div style="background:#fff; border:1px solid #eee; padding:20px; border-radius:6px; color:#333; font-size:15px; line-height:1.6;">
            "${formData.comment}"
         </div>
         <div style="margin-top:10px; font-size:12px; color:#888;">
           (Auto-translated to: ${Object.keys(localizedComment).filter(k => k !== currentLang).join(', ')})
         </div>
         `,
        undefined, // replyTo
        `${window.location.origin}/admin`, // actionUrl
        "Approve in Dashboard" // actionText
      );

      // Reset form
      setFormData({ reviewer_name: '', comment: '' });
      setRating(5);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: t('common.errorTitle', "Error"),
        description: t('reviews.submitError', "Failed to submit review. Please try again."),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>{t('reviews.formTitle', 'Leave a Review')}</CardTitle>
        <CardDescription>{t('reviews.formSubtitle', 'Share your experience with us')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="reviewer_name">{t('reviews.yourName', 'Your Name')}</Label>
            <Input
              id="reviewer_name"
              value={formData.reviewer_name}
              onChange={(e) => setFormData(prev => ({ ...prev, reviewer_name: e.target.value }))}
              placeholder={t('reviews.namePlaceholder', 'John Doe')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>{t('reviews.rating', 'Rating')}</Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`h-8 w-8 ${star <= (hoveredRating || rating)
                      ? 'text-[hsl(var(--gold))] fill-current'
                      : 'text-muted-foreground'
                      }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="comment">{t('reviews.yourReview', 'Your Review')}</Label>
            <Textarea
              id="comment"
              value={formData.comment}
              onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
              placeholder={t('reviews.reviewPlaceholder', 'Tell us about your experience...')}
              rows={4}
              required
            />
          </div>

          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? t('reviews.submitting', 'Submitting...') : t('reviews.submit', 'Submit Review')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}